import FormControl from '@mui/material/FormControl';
import { SxProps, Theme } from '@mui/material/styles';
import {
  DateTimePicker,
  DateTimePickerProps,
  DateView,
  renderTimeViewClock,
} from '@mui/x-date-pickers';
import { Dayjs } from 'dayjs';
import { ReactElement, useCallback, useId } from 'react';
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  useFormContext,
} from 'react-hook-form';

import CalendarIcon from '@/assets/icons/common/CalendarIcon.png';
import { useFormProviderRCPropsContext } from '@/components/form/FormProviderRC.comp.tsx';
import { isFormFieldRequired } from '@/utils/form/isFormFieldRequired.util.ts';

import { FormFieldLabel } from './FormFieldLabel.comp';
import { FormPropsModel } from './model/FormProps.model';
import { TextError } from './TextError.comp';

interface Props<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> extends FormPropsModel<TName> {
  /** Fields validated again after this field changes; applies once the form has been submitted. */
  deps?: FieldPath<TFieldValues>[];
  disableFuture?: boolean;
  disablePast?: boolean;
  displayWeekNumber?: boolean;
  format?: string;
  maxDateTime?: Dayjs;
  minDateTime?: Dayjs;
  onChangeSideEffect?: (value: Dayjs | null) => void;
  textFieldSx?: SxProps<Theme>;
  timezone?: DateTimePickerProps['timezone'];
  viewRenderers?: DateTimePickerProps['viewRenderers'];
  views?: DateView[];
}

const OpenPickerIcon = () => <img alt="Pick" src={CalendarIcon} />;
const renderMinutesClock: typeof renderTimeViewClock = props =>
  renderTimeViewClock({ ...props, minutesStep: 5 });

export const DateTimeFormField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>(
  props: Props<TFieldValues, TName>,
): ReactElement<Props<TFieldValues, TName>> => {
  const { control } = useFormContext<TFieldValues>();
  const {
    deps,
    format,
    fullWidth,
    label,
    name,
    onChangeSideEffect,
    placeholder,
    textFieldSx,
    ...rest
  } = props;
  const { validation } = useFormProviderRCPropsContext();
  const errorId = useId();
  const render: ControllerProps<TFieldValues, TName>['render'] = useCallback(
    ({ field, fieldState: { error } }) => {
      const isRequired = isFormFieldRequired(field.name, validation);

      return (
        <FormControl fullWidth={fullWidth ?? true} sx={{ mt: 2 }}>
          <FormFieldLabel isRequired={isRequired} label={label} />
          <DateTimePicker
            {...field}
            {...rest}
            format={format ?? 'YYYY/MM/DD HH:mm'}
            onChange={value => {
              if (onChangeSideEffect) {
                onChangeSideEffect(value);
              }
              field.onChange(value);
            }}
            slotProps={{
              textField: {
                error: Boolean(error),
                hiddenLabel: true,
                slotProps: {
                  htmlInput: { placeholder: placeholder ?? 'Choose date' },
                  input: {
                    disableUnderline: true,
                    ...(error && { 'aria-describedby': errorId }),
                  },
                },
                sx: textFieldSx,
                variant: 'standard',
              },
            }}
            slots={{
              openPickerIcon: OpenPickerIcon,
            }}
            value={field.value ?? null}
            viewRenderers={{
              hours: renderTimeViewClock,
              minutes: renderMinutesClock,
              seconds: renderTimeViewClock,
            }}
          />
          <TextError error={error} id={errorId} />
        </FormControl>
      );
    },
    [
      errorId,
      format,
      fullWidth,
      label,
      onChangeSideEffect,
      placeholder,
      rest,
      textFieldSx,
      validation,
    ],
  );

  return (
    <Controller<TFieldValues, TName>
      control={control}
      name={name}
      render={render}
      rules={{ deps }}
    />
  );
};
